import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import { eq, not, or } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import Modal from 'umg-ember-backend/components/ui/modal';
import Window from 'umg-ember-backend/components/ui/window';

import Crop from './crop';
import Download from './download';
import EmbedCode from './embed-code';
import PreviewEditor from './preview-editor';
import Replace from './replace';
import Sampling from './sampling';

export default class RecordDetailFormsAssetInfoAssetToolsComponent extends Component {
  // Services
  @service flashMessages;
  @service intl;
  @service recordStatus;

  // Defaults
  @tracked selectedAssetTool = null;

  // Getter and setter
  get isSamplingPossible() {
    return [1, 2].includes(this.args.asset.cmsState);
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.asset);
  }

  get winodwHasSidebar() {
    return ['crop', 'preview_editor'].includes(this.selectedAssetTool);
  }

  // Functions
  copySuccess = () => {
    this.flashMessages.success('flash.asset_copy.success');
  };

  copyError = () => {
    this.flashMessages.error('flash.asset_copy.error');
  };

  toggleAssetTool = (tool) => {
    this.selectedAssetTool = tool;
  };

  // Template
  <template>
    <div ...attributes>
      {{#unless this.isSamplingPossible}}
        <InfoBox @type='warning' data-test-asset-tool-warning='state'>
          {{t 'asset_tools.state_warning'}}
        </InfoBox>
      {{/unless}}

      {{#if @asset.isVideo}}
        <Button
          @displayBlock={{true}}
          @icon='replace'
          @label={{t 'asset_tools.actions.replace'}}
          disabled={{this.isDisabled}}
          data-test-asset-tool-button='replace'
          {{on 'click' (fn this.toggleAssetTool 'replace')}}
        />
      {{/if}}

      {{#if @asset.isVideo}}
        <Button
          @displayBlock={{true}}
          @icon='edit'
          @label={{t 'asset_tools.actions.preview_editor'}}
          disabled={{this.isDisabled}}
          data-test-asset-tool-button='preview-editor'
          {{on 'click' (fn this.toggleAssetTool 'preview_editor')}}
        />
      {{/if}}

      {{#if (or @asset.isVideo @asset.isImage)}}
        <Button
          @displayBlock={{true}}
          @icon='crop'
          @label={{t 'asset_tools.actions.crop'}}
          disabled={{this.isDisabled}}
          data-test-asset-tool-button='crop'
          {{on 'click' (fn this.toggleAssetTool 'crop')}}
        />
      {{/if}}

      <Button
        @displayBlock={{true}}
        @icon='download'
        @label={{t 'asset_tools.actions.download'}}
        disabled={{or this.isDisabled (not this.isSamplingPossible)}}
        data-test-asset-tool-button='download'
        {{on 'click' (fn this.toggleAssetTool 'download')}}
      />

      {{#if @asset.isVideo}}
        <Button
          @displayBlock={{true}}
          @icon='preview'
          @label={{t 'asset_tools.actions.sampling'}}
          disabled={{or this.isDisabled (not this.isSamplingPossible)}}
          data-test-asset-tool-button='sampling'
          {{on 'click' (fn this.toggleAssetTool 'sampling')}}
        />

        <Button
          @displayBlock={{true}}
          @icon='code'
          @label={{t 'asset_tools.actions.embed_code'}}
          disabled={{or this.isDisabled (not this.isSamplingPossible)}}
          data-test-asset-tool-button='embed-code'
          {{on 'click' (fn this.toggleAssetTool 'embed_code')}}
        />
      {{/if}}

      <Modal
        @dataDependencies={{this.selectedAssetTool}}
        @onClose={{fn this.toggleAssetTool null}}
        @isFullHeight={{this.winodwHasSidebar}}
        as |selectedAssetTool|
      >
        <Window @isSeamless={{this.winodwHasSidebar}}>
          <:title>
            {{t (concat 'asset_tools.' selectedAssetTool '.headline')}}
          </:title>

          <:description>
            {{t (concat 'asset_tools.' selectedAssetTool '.description')}}
          </:description>

          <:body>
            {{#if (eq selectedAssetTool 'crop')}}
              <Crop @asset={{@asset}} />
            {{/if}}

            {{#if (eq selectedAssetTool 'download')}}
              <Download
                @asset={{@asset}}
                @copyError={{this.copyError}}
                @copySuccess={{this.copySuccess}}
              />
            {{/if}}

            {{#if (eq selectedAssetTool 'embed_code')}}
              <EmbedCode
                @asset={{@asset}}
                @copyError={{this.copyError}}
                @copySuccess={{this.copySuccess}}
              />
            {{/if}}

            {{#if (eq selectedAssetTool 'preview_editor')}}
              <PreviewEditor @asset={{@asset}} />
            {{/if}}

            {{#if (eq selectedAssetTool 'replace')}}
              <Replace @asset={{@asset}} />
            {{/if}}

            {{#if (eq selectedAssetTool 'sampling')}}
              <Sampling
                @asset={{@asset}}
                @copyError={{this.copyError}}
                @copySuccess={{this.copySuccess}}
              />
            {{/if}}
          </:body>
        </Window>
      </Modal>
    </div>
  </template>
}
